import { lastPageNumber } from '@/components/data-table/pagination-math';
import { LIST_PAGE_SIZE_OPTIONS, loadListPageSize } from '@/lib/list-page-size';
import { urlPage, urlParam } from '@/lib/url-state/codecs';

/** The size a list shows before anyone chooses one. */
export const DEFAULT_LIST_PAGE_SIZE = 20;

/** A size from a URL or storage, if the table offers it; otherwise null. */
export function offeredPageSize(
  raw: number | 'all' | null | undefined,
  options: readonly number[]
): number | null {
  return typeof raw === 'number' && options.includes(raw) ? raw : null;
}

/** `page` (1-based) and `limit` from a URL; a size the table doesn't offer is ignored. */
export function readTablePagingParams(
  params: URLSearchParams | null | undefined,
  options: readonly number[] = LIST_PAGE_SIZE_OPTIONS
): { page: number; urlPageSize: number | null } {
  if (!params) return { page: 1, urlPageSize: null };
  return {
    page: TABLE_PAGING_URL.page.read(params) ?? 1,
    urlPageSize: offeredPageSize(TABLE_PAGING_URL.limit.read(params), options),
  };
}

/**
 * Write `page` and `limit` into a URL being built: `page` only past page 1, `limit` only when it
 * isn't the default (a limit in the URL replaces the size the viewer remembered).
 */
export function writeTablePagingParams(
  params: URLSearchParams,
  { page, pageSize, defaultPageSize = DEFAULT_LIST_PAGE_SIZE }: {
    page: number;
    pageSize: number;
    defaultPageSize?: number;
  }
): void {
  if (page > 1) params.set('page', String(page));
  else params.delete('page');
  if (pageSize !== defaultPageSize) params.set('limit', String(pageSize));
  else params.delete('limit');
}

/** The size to show: fixed, then chosen on this visit, then the URL's, the remembered one, the default. */
export function resolveTablePageSize({
  fixedPageSize,
  chosen,
  urlPageSize,
  saved,
  defaultPageSize,
}: {
  fixedPageSize?: number;
  chosen: number | null;
  urlPageSize: number | null;
  saved: number | null;
  defaultPageSize: number;
}): number {
  return fixedPageSize ?? chosen ?? urlPageSize ?? saved ?? defaultPageSize;
}

/** The remembered size for a list, if the table still offers it. */
export function loadTablePageSize(
  storageKey: string | null,
  options: readonly number[]
): number | null {
  return offeredPageSize(loadListPageSize(storageKey), options);
}

/**
 * The page to move to once an answer has landed: the last page when the list is shorter than the
 * page asked for (a stale `?page=`, rows removed), otherwise null. Never while loading or before
 * the first answer, so a page restored from the URL survives until the rows arrive.
 */
export function clampedPage({
  page,
  pageSize,
  total,
  loading,
}: {
  page: number;
  pageSize: number;
  total: number | undefined;
  loading: boolean;
}): number | null {
  if (loading || total === undefined) return null;
  const last = lastPageNumber(total, pageSize);
  return page > last ? last : null;
}

/**
 * `page` and `limit` for a page on `useUrlState`: spread into its schema
 * (`{ ...TABLE_PAGING_URL, q: urlText('q'), … }`) and hand them to `useTablePaging`'s `url` option,
 * so the URL is the source of truth for the page too. Neither is remembered (resolution 10): the
 * size lives in storage, and a `limit` in the URL applies to that visit only. `limit` is null when
 * the URL has none; useTablePaging ignores a size the table doesn't offer.
 *
 * `tablePagingUrl` gives the same fields under other keys, for a section that shares its page's
 * URL with another table (the planning orders list on /planning).
 */
export function tablePagingUrl(
  keys: { page: string; limit: string; limitAliases?: readonly string[] } = {
    page: 'page',
    limit: 'limit',
    limitAliases: ['pageSize'],
  }
) {
  return {
    page: urlPage(keys.page),
    limit: urlParam<number | null>({
      key: keys.limit,
      aliases: keys.limitAliases,
      default: null,
      remember: false,
      parse(raw) {
        const n = Number(raw);
        return Number.isInteger(n) && n >= 1 ? n : undefined;
      },
      format: String,
    }),
  };
}

export const TABLE_PAGING_URL = tablePagingUrl();
